import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    UserBasic,
} from "../models";
import { useAuth, useQueryParams } from "../contexts";
import { getRequestOptions } from "../helper-functions";

interface IUseUserBasicQuery extends Pick<IUseQueryPaged<UserBasic>, "pagedListQuery" | "listQuery" | "detailQuery"> {}

interface IUserBasicQueryParams extends QueryHookParams {
    roleIds?: number[];
    id?: string | number;
    ids?: number[];
    includeInactive?: boolean;
}

export const useUserBasicQuery = (params?: IUserBasicQueryParams): IUseUserBasicQuery => {
    const { isAuthorized } = useAuth();
    const { get } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Users);
    const url = `${Endpoints.Users}/basic`;
    const queryParamsWithArgs = {
        ...queryParams,
        roleIds: params?.roleIds ? params.roleIds.join(",") : "",
        id: params?.id?.toString() ?? queryParams.id,
        ids: params?.ids ? params.ids.join(",") : "",
    };
    const includeInactive = params?.includeInactive ?? false;
    const queryType = params?.queryType ?? QueryType.List;

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<UserBasic>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, pageParams, includeInactive)
            ),
        queryKey: queryKeys.usersBasic.pagedList({
            ...queryParamsWithArgs,
            ...pageParams,
            ...sortParams,
            includeInactive,
        }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<UserBasic>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, undefined, includeInactive, true)
            ).then((response) => response.items),
        queryKey: queryKeys.usersBasic.list({ ...queryParamsWithArgs, ...sortParams, includeInactive }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<UserBasic>({ url: `${url}/${params?.id}`, config }),
        queryKey: queryKeys.usersBasic.detail(params?.id ?? 0),
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    return useMemo<IUseUserBasicQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
        }),
        [pagedListQuery, listQuery, detailQuery]
    );
};
